import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { filesApi, UploadedFile, FileCategory, OwnerType, Visibility } from '@/api/http-client';

export interface UseUploadFileOptions {
  ownerId: string;
  ownerType: OwnerType;
  category: FileCategory;
  visibility?: Visibility;
  expiresInDays?: number;
  onSuccess?: (file: UploadedFile) => void;
  onError?: (error: Error) => void;
}

export interface UseUploadFileResult {
  upload: (file: File) => Promise<UploadedFile>;
  isUploading: boolean;
  progress: number;
  error: Error | null;
}

export function useUploadFile(options: UseUploadFileOptions): UseUploadFileResult {
  const {
    ownerId,
    ownerType,
    category,
    visibility = 'PRIVATE',
    expiresInDays,
    onSuccess,
    onError,
  } = options;

  const queryClient = useQueryClient();
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const upload = async (file: File): Promise<UploadedFile> => {
    setIsUploading(true);
    setProgress(0);
    setError(null);

    try {
      setProgress(30);

      const result = await filesApi.uploadFile(
        file,
        ownerId,
        ownerType,
        category,
        visibility,
        expiresInDays,
      );

      setProgress(100);
      setIsUploading(false);
      onSuccess?.(result);
      queryClient.invalidateQueries({ queryKey: ['files', ownerId] });

      return result;
    } catch (err) {
      const uploadError = err instanceof Error ? err : new Error('Upload failed');
      setError(uploadError);
      setIsUploading(false);
      onError?.(uploadError);
      throw uploadError;
    }
  };

  return {
    upload,
    isUploading,
    progress,
    error,
  };
}
